import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { RealWorldConnectionCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const TargetSkillsStep: React.FC<
  StepComponentProps<RealWorldConnectionCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomSkill, setShowCustomSkill] = useState(false);

  const targetSkillOptions = [
    {
      id: "Problem Çözme",
      emoji: "🧩",
      label: t("realWorld.skill.problemSolving"),
      desc: t("realWorld.skill.problemSolvingDesc"),
    },
    {
      id: "Eleştirel Düşünme",
      emoji: "🤔",
      label: t("realWorld.skill.criticalThinking"),
      desc: t("realWorld.skill.criticalThinkingDesc"),
    },
    {
      id: "Yaratıcılık",
      emoji: "🎨",
      label: t("realWorld.skill.creativity"),
      desc: t("realWorld.skill.creativityDesc"),
    },
    {
      id: "İletişim",
      emoji: "💬",
      label: t("realWorld.skill.communication"),
      desc: t("realWorld.skill.communicationDesc"),
    },
    {
      id: "İşbirliği",
      emoji: "🤝",
      label: t("realWorld.skill.collaboration"),
      desc: t("realWorld.skill.collaborationDesc"),
    },
    {
      id: "Liderlik",
      emoji: "👥",
      label: t("realWorld.skill.leadership"),
      desc: t("realWorld.skill.leadershipDesc"),
    },
    {
      id: "Araştırma",
      emoji: "🔍",
      label: t("realWorld.skill.research"),
      desc: t("realWorld.skill.researchDesc"),
    },
    {
      id: "Teknoloji Kullanımı",
      emoji: "💻",
      label: t("realWorld.skill.technology"),
      desc: t("realWorld.skill.technologyDesc"),
    },
  ];

  const handleSkillToggle = (skillId: string) => {
    const currentSkills = data.targetSkills || [];
    const isSelected = currentSkills.includes(skillId);

    if (isSelected) {
      const newSkills = currentSkills.filter((id) => id !== skillId);
      onDataChange({ targetSkills: newSkills });
    } else {
      const newSkills = [...currentSkills, skillId];
      onDataChange({ targetSkills: newSkills });
    }
  };

  const handleCustomSkillToggle = () => {
    const currentSkills = data.targetSkills || [];
    const hasCustomSkill = currentSkills.some(
      (skill) => !targetSkillOptions.some((option) => option.id === skill),
    );

    if (hasCustomSkill || showCustomSkill) {
      const newSkills = currentSkills.filter((skill) =>
        targetSkillOptions.some((option) => option.id === skill),
      );
      onDataChange({ targetSkills: newSkills });
      setShowCustomSkill(false);
    } else {
      setShowCustomSkill(true);
    }
  };

  const handleCustomSkillChange = (value: string) => {
    const currentSkills = data.targetSkills || [];
    const filteredSkills = currentSkills.filter((skill) =>
      targetSkillOptions.some((option) => option.id === skill),
    );

    if (value.trim()) {
      onDataChange({ targetSkills: [...filteredSkills, value.trim()] });
    } else {
      onDataChange({ targetSkills: filteredSkills });
    }
  };

  const isSkillSelected = (skillId: string) => {
    return (data.targetSkills || []).includes(skillId);
  };

  const isCustomSkillSelected = () => {
    const currentSkills = data.targetSkills || [];
    return (
      showCustomSkill ||
      currentSkills.some(
        (skill) => !targetSkillOptions.some((option) => option.id === skill),
      )
    );
  };

  const getCustomSkillValue = () => {
    const currentSkills = data.targetSkills || [];
    const customSkill = currentSkills.find(
      (skill) => !targetSkillOptions.some((option) => option.id === skill),
    );
    return customSkill || "";
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("realWorld.steps.skills.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("realWorld.steps.skills.description")}
        </p>
      </div>

      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("realWorld.form.targetSkills")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("realWorld.form.targetSkillsDesc")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {targetSkillOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isSkillSelected(option.id)
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleSkillToggle(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                    isSkillSelected(option.id)
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {isSkillSelected(option.id) && (
                    <div className="w-2.5 h-2.5 bg-white rounded-sm"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{option.emoji}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Custom Skill Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isCustomSkillSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleCustomSkillToggle}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                  isCustomSkillSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isCustomSkillSelected() && (
                  <div className="w-2.5 h-2.5 bg-white rounded-sm"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">✏️</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {t("realWorld.skill.other")}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("realWorld.skill.otherDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Skill Input */}
        {isCustomSkillSelected() && (
          <div className="mt-4">
            <label
              className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
            >
              {t("realWorld.form.customSkill")}
            </label>
            <input
              type="text"
              value={getCustomSkillValue()}
              onChange={(e) => handleCustomSkillChange(e.target.value)}
              className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
              placeholder={t("realWorld.form.customSkillPlaceholder")}
            />
          </div>
        )}
      </div>
    </div>
  );
};
